// AddLeaderWizard.tsx
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Title, Text, Stack, Group, Card, Avatar, ActionIcon, Badge, Textarea,
  Switch, MultiSelect, Divider, Box, Center, Loader,
} from '@mantine/core';
import { IconArrowLeft, IconSearch, IconUserPlus, IconUserMinus } from '@tabler/icons-react';
import { Checkbox, Select, TextInput } from '@/components';
import MultiStepForm, { FormStep } from '@/components/advanced/forms/MultiStepForm';
import { toast } from '@/components/ui/providers/AlertProvider';
import { LEADER_BIO_MAX_LENGTH } from '../constants';
import {
  useLeaderService,
  CreateLeader,
  useUserService,
  UserModelView,
  useLeaderTypeService,
  LeaderType,
  useCareZoneService,
  useLocationService,
  useCareNeedService,
} from '@actschurch/ccms-sdk';

interface CreateLeaderExtended extends CreateLeader {
  zoneIDs: number[];
  locationIDs: number[];
  needIDs: number[];
}

const userDisplayName = (user: UserModelView) =>
  user.fullName?.trim() ||
  [user.firstName, user.surname].filter(Boolean).join(' ') ||
  user.email ||
  'Unknown';

export default function AddLeaderWizard() {
  const navigate = useNavigate();

  const Leaders     = useLeaderService();
  const Users       = useUserService();
  const LeaderTypes = useLeaderTypeService();
  const CareZones   = useCareZoneService();
  const Locations   = useLocationService();
  const CareNeeds   = useCareNeedService();

  const [submitting, setSubmitting] = useState(false);

  // ── Lookups ──────────────────────────────────────────────────────────────
  const [types, setTypes]               = useState<LeaderType[]>([]);
  const [allZones, setAllZones]         = useState<any[]>([]);
  const [allLocations, setAllLocations] = useState<any[]>([]);
  const [allNeeds, setAllNeeds]         = useState<any[]>([]);
  const [existingLeaderUserIds, setExistingLeaderUserIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    Promise.all([
      LeaderTypes.getAll(),
      CareZones.getAll(),
      Locations.getAll(),
      CareNeeds.getAll(),
      Leaders.list(),
    ]).then(([leaderTypes, zones, locations, needs, leaders]) => {
      setTypes(leaderTypes);
      setAllZones(zones);
      setAllLocations(locations);
      setAllNeeds(needs);
      setExistingLeaderUserIds(new Set(leaders.map((l) => l.userId).filter(Boolean)));
    }).catch(console.error);
  }, [LeaderTypes, CareZones, Locations, CareNeeds, Leaders]);

  // ── Step 1: user search + staging ──────────────────────────────────────
  const [memberSearch, setMemberSearch]           = useState('');
  const [users, setUsers]                         = useState<UserModelView[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [stagedUserIds, setStagedUserIds]         = useState<Set<string>>(new Set());

  const fetchUsers = useCallback(async () => {
    if (!memberSearch.trim()) { setUsers([]); return; }
    setUserSearchLoading(true);
    try {
      setUsers(await Users.search({ searchTerm: memberSearch }));
    } catch (e) {
      console.error(e);
    } finally {
      setUserSearchLoading(false);
    }
  }, [Users, memberSearch]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const toggleStageUser = (id: string) => {
    setStagedUserIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const searchedUsers = users.filter((u) => {
    if (!u.id) return false;
    if (existingLeaderUserIds.has(u.id) || stagedUserIds.has(u.id)) return false;
    return true;
  });

  const stagedUsersList = users.filter((u) => u.id && stagedUserIds.has(u.id));

  // ── Step 2: default settings ────────────────────────────────────────────
  const [typeID, setTypeID]                     = useState<number>(1);
  const [bio, setBio]                           = useState('');
  const [supportsVirtual, setSupportsVirtual]   = useState(true);
  const [supportsInPerson, setSupportsInPerson] = useState(true);
  const [professional, setProfessional] = useState(false);

  // ── Step 3: zones / locations / needs (multi-select) ───────────────────
  const [zoneIDs, setZoneIDs]         = useState<string[]>([]);
  const [locationIDs, setLocationIDs] = useState<string[]>([]);
  const [needIDs, setNeedIDs]         = useState<string[]>([]);

  const typeName = types.find((t) => t.typeID === typeID)?.name ?? `Type ${typeID}`;
  const zoneNames = allZones.filter((z) => zoneIDs.includes(String(z.zoneID))).map((z) => z.name ?? `Zone ${z.zoneID}`);
  const locationNames = allLocations.filter((l) => locationIDs.includes(String(l.locationID))).map((l) => l.name ?? l.locationName ?? `Location ${l.locationID}`);
  const needNames = allNeeds.filter((n) => needIDs.includes(String(n.needID))).map((n) => n.name ?? `Need ${n.needID}`);

  // ── Submit ───────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (stagedUserIds.size === 0) return;
    setSubmitting(true);
    try {
      await Promise.all(
        Array.from(stagedUserIds).map((userId) => {
          const payload: CreateLeaderExtended = {
            userId,
            memberID: 0,
            typeID,
            bio,
            supportsVirtual,
            supportsInPerson,
            professional,
            zoneIDs: zoneIDs.map(Number),
            locationIDs: locationIDs.map(Number),
            needIDs: needIDs.map(Number),
          };
          return Leaders.create(payload);
        })
      );
      toast.success('Leaders Added', `Successfully added ${stagedUserIds.size} new leader(s).`);
      navigate('/Information/CareSupportLeaders');
    } catch (e) {
      console.error(e);
      toast.error('Failed', 'Could not add leaders. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Step validation ──────────────────────────────────────────────────
  const validateStep1 = () => {
    if (stagedUserIds.size === 0) {
      toast.error('No Users Selected', 'Add at least one person to continue.');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!typeID) {
      toast.error('Missing Leader Type', 'Please select a leader type.');
      return false;
    }
    return true;
  };

  // ── Step content ─────────────────────────────────────────────────────
  const Step1 = (
    <Stack gap="xs">
      {stagedUserIds.size > 0 && (
        <Stack gap={6} mb="xs">
          <Text size="sm" fw={600}>To Be Added ({stagedUserIds.size})</Text>
          {stagedUsersList.map((user) => {
            const name = userDisplayName(user);
            return (
              <Card key={user.id} withBorder radius="md" padding="sm">
                <Group justify="space-between">
                  <Group gap="md">
                    <Avatar src={user.avatar} alt={name} size="md" radius="xl">
                      {!user.avatar && name.charAt(0).toUpperCase()}
                    </Avatar>
                    <div>
                      <Text size="sm" fw={600}>{name}</Text>
                      <Text size="sm" c="dimmed">{user.email}</Text>
                    </div>
                  </Group>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="lg"
                    radius="md"
                    onClick={() => user.id && toggleStageUser(user.id)}
                  >
                    <IconUserMinus size={18} />
                  </ActionIcon>
                </Group>
              </Card>
            );
          })}
        </Stack>
      )}

      <Divider label="Search People" labelPosition="center" my="sm" />

      <TextInput
        placeholder="Search by name or email"
        value={memberSearch}
        onChange={(e) => setMemberSearch(e.currentTarget.value)}
        leftSection={<IconSearch size={16} />}
        radius="md"
      />

      {userSearchLoading && <Center py="sm"><Loader size="sm" /></Center>}
      {memberSearch.trim().length > 0 && !userSearchLoading && (
        <Text size="xs" c="dimmed">
          {searchedUsers.length} result{searchedUsers.length !== 1 ? 's' : ''}
        </Text>
      )}

      <Stack gap={6}>
        {searchedUsers.map((user) => {
          const name = userDisplayName(user);
          return (
            <Card key={user.id} withBorder radius="md" padding="sm">
              <Group justify="space-between">
                <Group gap="md">
                  <Avatar src={user.avatar} alt={name} size="md" radius="xl">
                    {!user.avatar && name.charAt(0).toUpperCase()}
                  </Avatar>
                  <div>
                    <Text size="sm" fw={600}>{name}</Text>
                    <Text size="sm" c="dimmed">{user.email}</Text>
                  </div>
                </Group>
                <ActionIcon
                  variant="subtle"
                  color="green"
                  size="lg"
                  radius="md"
                  onClick={() => user.id && toggleStageUser(user.id)}
                >
                  <IconUserPlus size={18} />
                </ActionIcon>
              </Group>
            </Card>
          );
        })}
      </Stack>
    </Stack>
  );

  const Step2 = (
    <Stack gap="md">
      <Select
        label="Leader Type"
        placeholder="Select a type"
        data={types.map((t) => ({ value: String(t.typeID), label: t.name ?? String(t.typeID) }))}
        value={String(typeID)}
        onChange={(value) => setTypeID(Number(value) || 1)}
      />
      <Textarea
        label="Biography"
        placeholder="Leader biography (optional)"
        value={bio}
        onChange={(e) => setBio(e.currentTarget.value.slice(0, LEADER_BIO_MAX_LENGTH))}
        maxLength={LEADER_BIO_MAX_LENGTH}
        description={`${bio.length}/${LEADER_BIO_MAX_LENGTH} characters`}
        minRows={3}
      />
      <Switch
        label="Supports Virtual"
        checked={supportsVirtual}
        onChange={(e) => setSupportsVirtual(e.currentTarget.checked)}
      />
      <Switch
        label="Supports In Person"
        checked={supportsInPerson}
        onChange={(e) => setSupportsInPerson(e.currentTarget.checked)}
      />
      <Checkbox
        label="Professional"
        checked={professional}
        onChange={(e) => setProfessional(e.currentTarget.checked)}
      />
    </Stack>
  );

  const Step3 = (
    <Stack gap="md">
      <MultiSelect
        label="Zones"
        placeholder="Select zones"
        data={allZones.map((z) => ({ value: String(z.zoneID), label: z.name ?? `Zone ${z.zoneID}` }))}
        value={zoneIDs}
        onChange={setZoneIDs}
        searchable
        clearable
      />
      <MultiSelect
        label="Locations"
        placeholder="Select locations"
        data={allLocations.map((l) => ({ value: String(l.locationID), label: l.name ?? l.locationName ?? `Location ${l.locationID}` }))}
        value={locationIDs}
        onChange={setLocationIDs}
        searchable
        clearable
      />
      <MultiSelect
        label="Needs"
        placeholder="Select needs"
        data={allNeeds.map((n) => ({ value: String(n.needID), label: n.name ?? `Need ${n.needID}` }))}
        value={needIDs}
        onChange={setNeedIDs}
        searchable
        clearable
      />
    </Stack>
  );

  const Step4 = (
    <Stack gap="md">
      <Box>
        <Text size="sm" fw={600} mb={4}>Leaders to be added ({stagedUsersList.length})</Text>
        <Stack gap={4}>
          {stagedUsersList.map((u) => (
            <Text key={u.id} size="sm">• {userDisplayName(u)} ({u.email})</Text>
          ))}
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Text size="sm" fw={600} mb={4}>Default Settings</Text>
        <Text size="sm">Type: <Badge variant="light">{typeName}</Badge></Text>
        <Text size="sm" c="dimmed" mt={4}>{bio || 'No bio provided'}</Text>
        <Group gap="xs" mt={6}>
          {supportsVirtual && <Badge color="blue" variant="light">Virtual</Badge>}
          {supportsInPerson && <Badge color="grape" variant="light">In Person</Badge>}
          {professional && <Badge color="teal" variant="light">Professional</Badge>}
        </Group>
      </Box>

      <Divider />

      <Box>
        <Text size="sm" fw={600} mb={4}>Zones</Text>
        {zoneNames.length === 0 ? <Text size="sm" c="dimmed">None selected</Text> : (
          <Group gap={4}>{zoneNames.map((n) => <Badge key={n} variant="light">{n}</Badge>)}</Group>
        )}
      </Box>
      <Box>
        <Text size="sm" fw={600} mb={4}>Locations</Text>
        {locationNames.length === 0 ? <Text size="sm" c="dimmed">None selected</Text> : (
          <Group gap={4}>{locationNames.map((n) => <Badge key={n} variant="light">{n}</Badge>)}</Group>
        )}
      </Box>
      <Box>
        <Text size="sm" fw={600} mb={4}>Needs</Text>
        {needNames.length === 0 ? <Text size="sm" c="dimmed">None selected</Text> : (
          <Group gap={4}>{needNames.map((n) => <Badge key={n} variant="light">{n}</Badge>)}</Group>
        )}
      </Box>
    </Stack>
  );

  const steps: FormStep[] = [
    { title: 'Add People', description: 'Search & select users', validate: validateStep1, content: Step1 },
    { title: 'Details', description: 'Type, bio & availability', validate: validateStep2, content: Step2 },
    { title: 'Assignment', description: 'Zones, locations, needs', content: Step3 },
    { title: 'Review', description: 'Confirm & submit', content: Step4 },
  ];

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" align="flex-start" mb="lg">
        <Stack gap={4}>
          <Title order={2}>Add Care Support Leader</Title>
          <Text c="dimmed">Search for a person and assign them as a leader.</Text>
        </Stack>
        <Group
          gap={6}
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/Information/CareSupportLeaders')}
        >
          <IconArrowLeft size={16} />
          <Text size="sm" c="blue">Back to Leaders</Text>
        </Group>
      </Group>

      <MultiStepForm
        steps={steps}
        onComplete={handleSubmit}
        onCancel={() => navigate('/Information/CareSupportLeaders')}
        completeLabel="Add Leaders"
        loading={submitting}
        allowSkip={false}
      />
    </Container>
  );
}